import React from 'react';
import { Field, FieldAttributes, FieldProps } from 'formik';
import { CreatableChipInput, CreatableChipInputProps } from './CreatableChipInput';
import { FormControl, FormHelperText } from '@mui/material';

type ChipInputFormFieldProps = Omit<CreatableChipInputProps, 'value' | 'onChange' | 'error' | 'helperText'>;

const CreatableChipInputFormFieldComponent: React.FC<FieldProps & CreatableChipInputProps> = ({
    label,
    description,
    options = [],
    disabled,
    limitTags,
    popperProps,
    placeholder,
    maxInputHeight,
    field,
    meta,
    onSelect
}) => {
    const isError = meta.touched && !!meta.error;
    const handleChange = React.useCallback(
        (value: string[]) => {
            const customEvent = {
                target: {
                    value,
                    name: field.name
                }
            };
            field.onChange(customEvent);
        },
        [field]
    );
    return (
        <FormControl fullWidth error={meta.touched && !!meta.error}>
            <CreatableChipInput
                label={label}
                options={options}
                disabled={disabled}
                limitTags={limitTags}
                popperProps={popperProps}
                placeholder={placeholder}
                onSelect={onSelect}
                maxInputHeight={maxInputHeight}
                value={field.value}
                onChange={handleChange}
                name={field.name}
            />
            {(isError || description) && (
                <FormHelperText sx={{ marginX: 0 }} error={isError}>
                    {isError ? meta.error : description}
                </FormHelperText>
            )}
        </FormControl>
    );
};

export const CreatableChipInputFormField: React.FC<FieldAttributes<ChipInputFormFieldProps>> = ({
    label,
    description,
    options = [],
    disabled,
    limitTags,
    popperProps,
    placeholder,
    maxInputHeight,
    onSelect,
    ...props
}) => (
    <Field {...props}>
        {(childProps: FieldProps) => (
            <CreatableChipInputFormFieldComponent
                label={label}
                description={description}
                options={options}
                disabled={disabled}
                limitTags={limitTags}
                popperProps={popperProps}
                placeholder={placeholder}
                onSelect={onSelect}
                maxInputHeight={maxInputHeight}
                value={childProps.field.value}
                error={childProps.meta.touched && !!childProps.meta.error}
                helperText={childProps.meta.error}
                name={childProps.field.name}
                {...childProps}
            />
        )}
    </Field>
);
